import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Trophy,
  Sparkles,
  TrendingUp,
  Target,
  Flame,
  Radio,
  Rocket,
  Award,
  Medal,
  Star,
  Users2,
  Heart,
  HandHeart,
  Activity,
  BarChart3,
  Gem
} from 'lucide-react';

const timeframes = [
  { value: '7d', label: '7 Days' },
  { value: '30d', label: '30 Days' },
  { value: '90d', label: '90 Days' },
  { value: 'all', label: 'All Time' }
];

const CategoryChip = ({ icon: Icon, label, color }) => (
  <div className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/[0.03] border border-white/5">
    <Icon className={`h-3.5 w-3.5 text-${color}-400`} />
    <span className="text-xs text-[#C7CED9]">{label}</span>
  </div>
);

const LeaderboardSection = ({
  icon: Icon,
  accentColor,
  title,
  categories,
  emptyTitle,
  emptySubtitle,
  testId
}) => (
  <Card className="glass border-white/5" data-testid={testId}>
    <CardHeader className="pb-3">
      <CardTitle className="text-base flex items-center gap-2 text-white">
        <Icon className={`h-5 w-5 text-${accentColor}-400`} />
        {title}
        <Badge variant="outline" className="ml-auto text-[10px] border-white/15 text-[#8A93A6]">
          Read-only
        </Badge>
      </CardTitle>
    </CardHeader>
    <CardContent className="pt-0">
      <div className="flex flex-wrap gap-2 mb-5">
        {categories.map((c) => (
          <CategoryChip key={c.label} icon={c.icon} label={c.label} color={c.color} />
        ))}
      </div>
      <div className="border-t border-white/5 pt-6 pb-4 text-center">
        <div className={`w-12 h-12 rounded-xl bg-${accentColor}-500/10 border border-white/5 flex items-center justify-center mx-auto mb-3`}>
          <Icon className={`h-6 w-6 text-${accentColor}-400`} />
        </div>
        <p className="text-sm font-medium text-[#C7CED9]">{emptyTitle}</p>
        <p className="text-xs text-[#8A93A6] mt-1 max-w-sm mx-auto">{emptySubtitle}</p>
      </div>
    </CardContent>
  </Card>
);

export default function ConnectLeaderboards() {
  const [timeframe, setTimeframe] = useState('30d');

  return (
    <div className="container mx-auto p-6 max-w-7xl" data-testid="connect-leaderboards-page">
      {/* Header */}
      <div className="mb-6 flex flex-wrap items-start justify-between gap-4">
        <div>
          <div className="flex flex-wrap items-center gap-3 mb-2">
            <Trophy className="h-6 w-6 text-yellow-400" />
            <h1 className="text-3xl font-bold text-white">Leaderboards</h1>
            <Badge variant="outline" className="text-[10px] border-yellow-500/30 text-yellow-300 uppercase tracking-wider font-semibold">
              Signature
            </Badge>
          </div>
          <p className="text-[#8A93A6] max-w-2xl">
            Rankings, streaks, rewards, and creator momentum across the Consistency ecosystem.
          </p>
        </div>

        {/* Timeframe Selector */}
        <div
          className="flex items-center gap-1 bg-white/5 rounded-lg p-0.5 self-start"
          data-testid="leaderboards-timeframe-selector"
        >
          {timeframes.map((tf) => (
            <button
              key={tf.value}
              onClick={() => setTimeframe(tf.value)}
              className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${
                timeframe === tf.value
                  ? 'bg-yellow-500/20 text-yellow-300'
                  : 'text-[#8A93A6] hover:text-white'
              }`}
              data-testid={`leaderboards-timeframe-${tf.value}`}
            >
              {tf.label}
            </button>
          ))}
        </div>
      </div>

      {/* Top Information Card — Creator Momentum System */}
      <div
        className="mb-8 rounded-xl border border-white/5 bg-gradient-to-br from-yellow-500/[0.05] via-transparent to-orange-500/[0.04] px-5 py-4"
        data-testid="creator-momentum-system-card"
      >
        <div className="flex items-start gap-3">
          <div className="w-9 h-9 rounded-lg bg-yellow-500/[0.08] border border-yellow-500/15 flex items-center justify-center shrink-0">
            <Sparkles className="h-4 w-4 text-yellow-300" />
          </div>
          <div className="space-y-1">
            <p className="text-sm font-semibold text-white">Creator Momentum System</p>
            <p className="text-xs text-[#8A93A6] leading-relaxed max-w-3xl">
              Leaderboards track creator consistency, growth, engagement, rewards, and performance across the platform. They power the Consistency Growth Flywheel — turning showing-up into measurable momentum.
            </p>
          </div>
        </div>
      </div>

      {/* Sections */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Section 1 — Top Creator Rankings */}
        <LeaderboardSection
          testId="leaderboard-top-creator-rankings"
          icon={Trophy}
          accentColor="yellow"
          title="Top Creator Rankings"
          categories={[
            { icon: Medal, label: 'Overall Ranking', color: 'yellow' },
            { icon: TrendingUp, label: 'Growth Ranking', color: 'green' },
            { icon: Target, label: 'Consistency Ranking', color: 'orange' },
            { icon: BarChart3, label: 'Results Ranking', color: 'blue' }
          ]}
          emptyTitle="No creator rankings yet"
          emptySubtitle="Rankings will populate as creators begin posting and building momentum."
        />

        {/* Section 2 — Streak Leaders */}
        <LeaderboardSection
          testId="leaderboard-streak-leaders"
          icon={Flame}
          accentColor="orange"
          title="Streak Leaders"
          categories={[
            { icon: Flame, label: 'Daily Posting Streaks', color: 'orange' },
            { icon: Radio, label: 'Live Streaming Streaks', color: 'red' },
            { icon: Rocket, label: 'Fastest Growth Streaks', color: 'yellow' }
          ]}
          emptyTitle="No streak leaders yet"
          emptySubtitle="Creators will appear here after consistent activity."
        />

        {/* Section 3 — Rewards & Badges */}
        <LeaderboardSection
          testId="leaderboard-rewards-badges"
          icon={Award}
          accentColor="amber"
          title="Rewards & Badges"
          categories={[
            { icon: Star, label: 'Top Badge Earners', color: 'amber' },
            { icon: Gem, label: 'Creator Tier Progress', color: 'violet' },
            { icon: Sparkles, label: 'Milestone Unlocks', color: 'pink' }
          ]}
          emptyTitle="No rewards activity yet"
          emptySubtitle="Badges and milestone rankings will appear here."
        />

        {/* Section 4 — Community Performance */}
        <LeaderboardSection
          testId="leaderboard-community-performance"
          icon={Users2}
          accentColor="blue"
          title="Community Performance"
          categories={[
            { icon: Heart, label: 'Engagement Leaders', color: 'pink' },
            { icon: HandHeart, label: 'Most Supportive Creators', color: 'purple' },
            { icon: Activity, label: 'Community Participation', color: 'cyan' }
          ]}
          emptyTitle="No community rankings yet"
          emptySubtitle="Community activity will begin populating after launch."
        />
      </div>

      <div className="mt-10 text-center">
        <p className="text-xs text-[#555E6E]">
          Leaderboards refresh as the Consistency ecosystem grows — keep showing up.
        </p>
      </div>
    </div>
  );
}
